import { Button, Collapse, List, ListItem, ListItemButton, ListItemButtonProps, ListItemIcon, ListItemText, ListProps, SwipeableDrawer, styled } from '@mui/material';
import { useCallback, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AutoFixHigh, ExpandLess, ExpandMore, Public as PublicIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import WebsiteStore from '../WebsiteStore';
import PreferencesDialog from './PreferencesDialog';
import { useAppSelector } from '../Common/_hooks/useAppSelector';
import type UserStoreStore from '../UserData/_stores/Store/UserStoreStore';
import HeaderLink from './HeaderNavigation';
import { getHelpCenterUrl } from '../Common/helpCenter';

const CustomSwipeableDrawer = styled(SwipeableDrawer)({
  top: '52px',
  '.MuiDrawer-paper': {
    backgroundColor: 'rgba(0, 0, 0, .95)',
    width: '100%',
    top: '52px',
    overflow: 'hidden',
  }
});

const ScrollableWrapper = styled('div')({
  paddingBottom: 200,
  overflow: 'auto',
});

const MainMenuList = styled(List)({

});

const AccountList = styled(List)({
  borderTop: '1px solid #3A3A3A',
});

const CollapseMobileMenu = styled(Collapse)({
  color: '#fff',
  borderBottom: '1px solid #3A3A3A',
  paddingTop: 0,
  transition: 'border-top 300ms, height 300ms',

  'span': {
    fontSize: 16,
  },

  '.MuiListItemText-root': {
    margin: 0,
  }
});

const CollapseListStyle = styled(List)({
  paddingBottom: 30
});

function CollapseList<C extends React.ElementType>(
  props: ListProps<C, { component?: C }>,
) {
  return <CollapseListStyle
    {...props}
  />
}

const ListItemMobileMenu = styled(ListItem)({
  color: '#fff',
  paddingTop: 0,
  paddingBottom: 0,
  borderTop: '1px solid transparent',
  transition: 'border-bottom 300ms',
  paddingLeft: 0,
  paddingRight: 0,

  'span': {
    fontSize: 22,
    paddingLeft: 15,
    paddingRight: 15,
  },

  '&.active': {
    color: '#F600BE',
    ':not(.account)': {
      borderTop: '1px solid #3A3A3A',
    },
    'span': {
      fontWeight: 500,
    }
  },
  '&.loginoutup': {
    'span': {
      color: '#fcee23',
    },
  },
});

const ListItemSiteSettingsBtn = styled(ListItem)({
  color: '#fff',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  borderTop: '1px solid #3A3A3A',
  marginTop: 15,
  'span': {
    fontSize: 18,
    paddingLeft: 15,
    paddingRight: 15,
  },

  '.MuiListItemIcon-root': {
    color: '#fff',
    minWidth: 20,
    marginLeft: 10,

    'svg': {
      fontSize: '2rem',
    }
  },
});


const ButtonsWrapper = styled('div')({
  position: 'absolute',
  bottom: 50,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'space-around',
  paddingBottom: 30,
  background: 'linear-gradient(to top, #000, transparent)',
});

const ButtonCreate = styled(Button)({
  display: 'block',
  borderRadius: 50,
  fontSize: 32,
  lineHeight: 1,
  fontFamily: 'Bebas Neue,sans-serif',
  textTransform: 'uppercase',
  backgroundColor: '#F600BC',
  backgroundImage: 'linear-gradient(to top, #F600BE, #EA008A)',
  color: '#fff',
  width: '50%',
  maxWidth: 300,
  height: 'auto',
  padding: '15px 20px 13px',
  margin: '0 7.5px 0 15px',
  textAlign: 'center',
  transition: 'background 250ms',
  '&:hover': {
    backgroundImage: 'none',
    backgroundColor: '#F600BC',
  }
});

const AccountTitle = styled('h5')({
  paddingLeft: 30,
  textTransform: 'uppercase',
  fontWeight: 700,
  marginBottom: 5,
  marginTop: 20,
});

const SubListItemButtonStyle = styled(ListItemButton)({
  paddingTop: 7,
  paddingBottom: 7,

  '&:hover': {
    color: '#FFF000',
  }
})

function SubListItemButton<C extends React.ElementType>(
  props: ListItemButtonProps<C, { component?: C }>,
) {
  return <SubListItemButtonStyle
    {...props}
  />
}

const Hamburger = styled(Button)(({ theme }) => ({
  display: 'block',
  minWidth: 25,
  alignSelf: 'stretch',
  borderRadius: 0,

  '.bar': {
    background: '#fff',
    display: 'block',
    height: 2,
    width: 25,
    borderRadius: 15,
    marginBottom: 5,

    '&:first-of-type': {
      marginTop: 5,
    }
  },
  [theme.breakpoints.up('md')]: {
    display: 'none',
  }
}));

export default function MobileNav() {
  const [t] = useTranslation('header');
  const [tR] = useTranslation('routes');

  const currency = useAppSelector(state => state.get('userData').get('prefs').get('currency'));
  const lang = useAppSelector(state => state.get('userData').get('prefs').get('lang'));
  const helpCenterUrl = getHelpCenterUrl(lang);
  const isLoggedIn = useAppSelector(state => state.get('userData').get('loggedIn'));
  const stores = useAppSelector(state => state.get('userData').get('stores'));
  const storeIntegrations = useAppSelector(state => state.get('appData').get('storeIntegrations'));
  const createUrl = useAppSelector(state => state.get('UIData').get('createUrl'))

  const [opened, setOpened] = useState(false);
  const [openedSubnav, setOpenedSubnav] = useState<'none' | 'info' | 'dropship' | 'policies' | 'account'>('none');
  const [openedStore, setOpenedStore] = useState('');
  const [prefDialogOpened, setPrefDialogOpened] = useState(false);

  const onToggle = useCallback(() => setOpened(value => !value), []);

  const onOpen = useCallback(() => setOpened(true), []);
  const onClose = useCallback(() => setOpened(false), []);

  const onPrefDialogOpen = useCallback(() => {
    setPrefDialogOpened(true)
  }, [])

  const onPrefDialogClose = useCallback(() => {
    setPrefDialogOpened(false)
  }, [])

  const renderStoreItem = useCallback((store: UserStoreStore) => {
    const storeSlug = store.get('slug');
    const storeIntegration = storeIntegrations.get(storeSlug);
    const hasSetupPage = !!storeIntegration && !storeIntegration.get('legacy');
    const isStoreOpened = openedStore === storeSlug;

    return <React.Fragment key={store.get('id')}>
      <SubListItemButton
        onClick={() => setOpenedStore(isStoreOpened ? '' : storeSlug)}
        sx={{
          pl: 4,
          pr: 2,
          '.MuiListItemText-primary': {
            fontWeight: 500,
          }
        }}
      >
        <ListItemText primary={store.get('name')} />
        {isStoreOpened ? <ExpandLess /> : <ExpandMore />}
      </SubListItemButton>
      <Collapse in={isStoreOpened} timeout="auto" unmountOnExit>
        <SubListItemButton component={HeaderLink} to={tR('/account/stores/{{store}}/overview', { store: storeSlug })} onClick={onClose} sx={{ pl: 6 }}>
          <ListItemText primary={t('Overview')} />
        </SubListItemButton>
        <SubListItemButton component={HeaderLink} to={tR('/account/stores/{{store}}/products', { store: storeSlug })} onClick={onClose} sx={{ pl: 6 }}>
          <ListItemText primary={t('Products')} />
        </SubListItemButton>
        <SubListItemButton component={HeaderLink} to={tR('/account/stores/{{store}}/orders', { store: storeSlug })} onClick={onClose} sx={{ pl: 6 }}>
          <ListItemText primary={t('Orders')} />
        </SubListItemButton>
        <SubListItemButton component={HeaderLink} to={tR('/account/stores/{{store}}/reports', { store: storeSlug })} onClick={onClose} sx={{ pl: 6 }}>
          <ListItemText primary={t('Purchase Report')} />
        </SubListItemButton>
        <SubListItemButton component={HeaderLink} to={tR('/account/stores/{{store}}/presets', { store: storeSlug })} onClick={onClose} sx={{ pl: 6 }}>
          <ListItemText primary={t('Presets')} />
        </SubListItemButton>
        {hasSetupPage ? <SubListItemButton component={HeaderLink} to={tR('/account/stores/connect/{{store}}', { store: storeSlug })} onClick={onClose} sx={{ pl: 6 }}>
          <ListItemText primary={t('Setup')} />
        </SubListItemButton> : null}
        <SubListItemButton component={HeaderLink} to={tR('/account/stores/{{store}}/settings', { store: storeSlug })} onClick={onClose} sx={{ pl: 6 }}>
          <ListItemText primary={t('Settings')} />
        </SubListItemButton>
      </Collapse>
    </React.Fragment>
  }, [onClose, openedStore, storeIntegrations, t, tR])

  return <>
    <Hamburger
      variant="contained"
      color="secondary"
      onClick={onToggle}
      aria-label={opened ? t('Close menu') : t('Open menu')}
      aria-expanded={opened}
    >
      <div className="bar"></div>
      <div className="bar"></div>
      <div className="bar"></div>
    </Hamburger>

    <CustomSwipeableDrawer
      open={opened}
      onOpen={onOpen}
      onClose={onClose}
      hideBackdrop={true}
      sx={{
        display: {
          xs: 'block',
          md: 'none'
        }
      }}
    >
      <ScrollableWrapper>
        <MainMenuList>
          <ListItemMobileMenu>
            <ListItemButton component={HeaderLink} to={tR('/products')} onClick={onClose}>
              <ListItemText primary={t('Products')} />
            </ListItemButton>
          </ListItemMobileMenu>
          <ListItemMobileMenu onClick={() => openedSubnav === 'info' ? setOpenedSubnav('none') : setOpenedSubnav('info')} className={openedSubnav === 'info' ? 'active' : ''}>
            <ListItemButton>
              <ListItemText primary={t('Infos')} />
              {openedSubnav === 'info' ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItemMobileMenu>
          <CollapseMobileMenu in={openedSubnav === 'info'} timeout="auto">
            <CollapseList component="div" disablePadding>
              <SubListItemButton component="a" href={tR('/drop-shipping')} sx={{ pl: 4 }}>
                <ListItemText primary={t('What is Drop Shipping')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/wholesale')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Wholesale')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/info/production-and-shipping')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Production and Shipping')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/info/pricing')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Pricing')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/info/design-guidelines')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Design Guidelines')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/custom-labels-and-packaging')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Custom Labels & Packaging')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/info/sample-packs')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Order Sample Packs')} />
              </SubListItemButton>
            </CollapseList>
          </CollapseMobileMenu>
          <ListItemMobileMenu onClick={() => openedSubnav === 'dropship' ? setOpenedSubnav('none') : setOpenedSubnav('dropship')} className={openedSubnav === 'dropship' ? 'active' : ''}>
            <ListItemButton>
              <ListItemText primary={t('Sell with Art of Where')} />
              {openedSubnav === 'dropship' ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItemMobileMenu>
          <CollapseMobileMenu in={openedSubnav === 'dropship'} timeout="auto">
            <CollapseList component="div" disablePadding>
              <SubListItemButton component="a" href={tR('/drop-shipping/etsy')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Sell with Etsy')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/drop-shipping/shopify')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Sell with Shopify')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/drop-shipping/squarespace')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Sell with Squarespace')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/drop-shipping/bigcartel')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Sell with Big Cartel')} />
              </SubListItemButton>
            </CollapseList>
          </CollapseMobileMenu>
          <ListItemMobileMenu onClick={() => openedSubnav === 'policies' ? setOpenedSubnav('none') : setOpenedSubnav('policies')} className={openedSubnav === 'policies' ? 'active' : ''}>
            <ListItemButton>
              <ListItemText primary={t('Policies')} />
              {openedSubnav === 'policies' ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItemMobileMenu>
          <CollapseMobileMenu in={openedSubnav === 'policies'} timeout="auto">
            <CollapseList component="div" disablePadding>
              <SubListItemButton component="a" href={tR('/info/return-policy')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Refund/Return')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/info/privacy-policy')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Privacy')} />
              </SubListItemButton>
              <SubListItemButton component="a" href={tR('/info/terms-of-service')} sx={{ pl: 4 }}>
                <ListItemText primary={t('Terms of Service')} />
              </SubListItemButton>
            </CollapseList>
          </CollapseMobileMenu>
          <ListItemMobileMenu>
            <ListItemButton component="a" href={helpCenterUrl} target="_blank" rel="noopener">
              <ListItemText primary={t('Help')} />
            </ListItemButton>
          </ListItemMobileMenu>
          <ListItemMobileMenu>
            <ListItemButton component="a" href="https://blog.artofwhere.com" target="_blank">
              <ListItemText primary={t('Blog')} />
            </ListItemButton>
          </ListItemMobileMenu>
        </MainMenuList>
        <AccountList>
          {isLoggedIn ? <>
            <ListItemMobileMenu onClick={() => openedSubnav === 'account' ? setOpenedSubnav('none') : setOpenedSubnav('account')} className={openedSubnav === 'account' ? 'active account' : ''}>
              <ListItemButton>
                <ListItemText primary={t('My account')} />
                {openedSubnav === 'account' ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItemMobileMenu>
            <CollapseMobileMenu in={openedSubnav === 'account'} timeout="auto">
              <CollapseList component="div" disablePadding>
                <AccountTitle>{t('Account')}</AccountTitle>
                <SubListItemButton component={HeaderLink} to={tR('/account/products')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Products')} />
                </SubListItemButton>
                <SubListItemButton component={HeaderLink} to={tR('/account/orders')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Orders')} />
                </SubListItemButton>
                <SubListItemButton component={HeaderLink} to={tR('/account/images')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Image Library')} />
                </SubListItemButton>
                <SubListItemButton component={HeaderLink} to={tR('/account/branding-options')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Branding options')} />
                </SubListItemButton>
                { /* <SubListItemButton component="a" href="" sx={{ pl: 4 }}>
                                    <ListItemText primary={t('Goals & Rewards')} />
                                </SubListItemButton> */ }
                <SubListItemButton component={HeaderLink} to={tR('/account/photo-lab')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Photo Lab')} />
                </SubListItemButton>

                <AccountTitle>{t('Settings')}</AccountTitle>
                <SubListItemButton component={HeaderLink} to={tR('/account/settings/info')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Personal info')} />
                </SubListItemButton>
                <SubListItemButton component={HeaderLink} to={tR('/account/settings/notifications')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Notifications')} />
                </SubListItemButton>
                <SubListItemButton component={HeaderLink} to={tR('/account/settings/security')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Security')} />
                </SubListItemButton>
                <SubListItemButton component={HeaderLink} to={tR('/account/settings/addresses')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Addresses')} />
                </SubListItemButton>
                <SubListItemButton component={HeaderLink} to={tR('/account/settings/credit-cards')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Credit cards')} />
                </SubListItemButton>
                <SubListItemButton component={HeaderLink} to={tR('/account/settings/tax-numbers')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Tax numbers')} />
                </SubListItemButton>

                <AccountTitle>{t('Stores')}</AccountTitle>
                {stores.valueSeq().map(renderStoreItem)}
                <SubListItemButton component={HeaderLink} to={tR('/account/stores')} onClick={onClose} sx={{ pl: 4 }}>
                  <ListItemText primary={t('Add Store')} />
                </SubListItemButton>
              </CollapseList>
            </CollapseMobileMenu>
          </> : null}
          {isLoggedIn ? <>
            <ListItemMobileMenu className="loginoutup">
              <ListItemButton component="a" href={tR('/auth/logout')}>
                <ListItemText primary={t('Logout')} />
              </ListItemButton>
            </ListItemMobileMenu>
          </> : <>
            <ListItemMobileMenu className="loginoutup">
              <ListItemButton component={HeaderLink} to={tR('/auth/login')} onClick={onClose}>
                <ListItemText primary={t('Login')} />
              </ListItemButton>
            </ListItemMobileMenu>
            <ListItemMobileMenu className="loginoutup">
              <ListItemButton component={HeaderLink} to={tR('/auth/signup')} onClick={onClose}>
                <ListItemText primary={t('Sign up')} />
              </ListItemButton>
            </ListItemMobileMenu>
          </>}
          <ListItemSiteSettingsBtn>
            <ListItemButton onClick={onPrefDialogOpen}>
              <ListItemIcon><PublicIcon /></ListItemIcon>
              <ListItemText primary={t('{{lang}} (${{currency}})', {
                lang: lang === 'fr_CA' ? 'FR' : 'EN',
                currency: currency
              })} />
            </ListItemButton>
          </ListItemSiteSettingsBtn>
        </AccountList>
      </ScrollableWrapper>
      <ButtonsWrapper>
        <ButtonCreate component={HeaderLink} to={createUrl !== '' ? createUrl : tR('/create')} reloadDocument>{t('Create')}</ButtonCreate>
      </ButtonsWrapper>
    </CustomSwipeableDrawer>

    <PreferencesDialog
      opened={prefDialogOpened}
      onClose={onPrefDialogClose}
    />
  </>
}
